/**
 * Color parse / serialize helpers for the alpha-capable ColorField.
 *
 * Editor-only: the stored value stays a raw CSS color string (apply.ts writes
 * it verbatim into the `--co-*` var, CSS natively accepts rgba/hsla/named).
 * These helpers let the editor offer an RGB swatch + an alpha slider on top of
 * a free-text field. Mirrors the null-on-unparseable contract of shadowValue.ts
 * — values we can't represent ({currentColor}, var(...), arbitrary names) parse
 * to `null` and the UI degrades gracefully (swatch/slider disabled, text kept).
 */

export interface Rgba {
  /** 0-255 */
  r: number;
  /** 0-255 */
  g: number;
  /** 0-255 */
  b: number;
  /** 0-1 */
  a: number;
}

function clamp(n: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, n));
}

function parseHex(input: string): Rgba | null {
  const raw = input.trim();
  if (raw[0] !== '#') return null;
  let h = raw.slice(1);
  if (!/^[0-9a-fA-F]+$/.test(h)) return null;
  // Expand shorthand #rgb / #rgba → #rrggbb / #rrggbbaa.
  if (h.length === 3 || h.length === 4) {
    h = h
      .split('')
      .map((c) => c + c)
      .join('');
  }
  if (h.length !== 6 && h.length !== 8) return null;
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  const a = h.length === 8 ? parseInt(h.slice(6, 8), 16) / 255 : 1;
  return { r, g, b, a };
}

function channel(token: string): number {
  if (token.endsWith('%')) return clamp(Math.round(parseFloat(token) * 2.55), 0, 255);
  return clamp(Math.round(parseFloat(token)), 0, 255);
}

function alphaToken(token: string | undefined): number {
  if (token === undefined) return 1;
  if (token.endsWith('%')) return clamp(parseFloat(token) / 100, 0, 1);
  return clamp(parseFloat(token), 0, 1);
}

function parseRgb(input: string): Rgba | null {
  const m = /^rgba?\(([^)]+)\)$/i.exec(input.trim());
  if (!m || m[1] === undefined) return null;
  // Accept comma syntax and the modern space + slash syntax.
  const parts = m[1].split(/[,/\s]+/).map((s) => s.trim()).filter(Boolean);
  const [p0, p1, p2, p3] = parts;
  if (p0 === undefined || p1 === undefined || p2 === undefined) return null;
  const r = channel(p0);
  const g = channel(p1);
  const b = channel(p2);
  const a = alphaToken(p3);
  if ([r, g, b, a].some(Number.isNaN)) return null;
  return { r, g, b, a };
}

function parseHsl(input: string): Rgba | null {
  const m = /^hsla?\(([^)]+)\)$/i.exec(input.trim());
  if (!m || m[1] === undefined) return null;
  const parts = m[1].split(/[,/\s]+/).map((s) => s.trim()).filter(Boolean);
  const [hs, ss, ls, as] = parts;
  if (hs === undefined || ss === undefined || ls === undefined) return null;
  const h = (((parseFloat(hs) % 360) + 360) % 360) / 360;
  const s = clamp(parseFloat(ss) / 100, 0, 1);
  const l = clamp(parseFloat(ls) / 100, 0, 1);
  const a = alphaToken(as);
  if ([h, s, l, a].some(Number.isNaN)) return null;

  const hue = (t: number): number => {
    let tt = t;
    if (tt < 0) tt += 1;
    if (tt > 1) tt -= 1;
    if (tt < 1 / 6) return p + (q - p) * 6 * tt;
    if (tt < 1 / 2) return q;
    if (tt < 2 / 3) return p + (q - p) * (2 / 3 - tt) * 6;
    return p;
  };
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  const r = s === 0 ? l : hue(h + 1 / 3);
  const g = s === 0 ? l : hue(h);
  const b = s === 0 ? l : hue(h - 1 / 3);
  return { r: Math.round(r * 255), g: Math.round(g * 255), b: Math.round(b * 255), a };
}

/**
 * Parse a CSS color string into RGBA. Handles hex (3/4/6/8), rgb()/rgba(),
 * hsl()/hsla(), and the `transparent` keyword. Returns `null` for anything we
 * can't safely represent on a swatch+slider (currentColor, var(...), inherit,
 * named colors) — callers then keep the raw text and disable the picker UI.
 */
export function parseColor(input: string): Rgba | null {
  if (!input) return null;
  const v = input.trim();
  if (v.toLowerCase() === 'transparent') return { r: 0, g: 0, b: 0, a: 0 };
  if (v[0] === '#') return parseHex(v);
  const lower = v.toLowerCase();
  if (lower.startsWith('rgb')) return parseRgb(v);
  if (lower.startsWith('hsl')) return parseHsl(v);
  return null;
}

function toHex2(n: number): string {
  return clamp(Math.round(n), 0, 255).toString(16).padStart(2, '0');
}

/** Full-opacity hex (drops alpha) — for the native `<input type=color>`. */
export function toHex6(c: Rgba): string {
  return `#${toHex2(c.r)}${toHex2(c.g)}${toHex2(c.b)}`;
}

/** Serialize RGBA back to a CSS string: `#rrggbb` at full opacity, else `rgba(...)`. */
export function serializeColor(c: Rgba): string {
  if (c.a >= 1) return toHex6(c);
  const a = Math.round(c.a * 100) / 100;
  return `rgba(${clamp(Math.round(c.r), 0, 255)}, ${clamp(Math.round(c.g), 0, 255)}, ${clamp(Math.round(c.b), 0, 255)}, ${a})`;
}

/** Replace RGB channels from a `#rrggbb` swatch value, preserving current alpha. */
export function setRgb(prev: Rgba, hex6: string): Rgba {
  const p = parseHex(hex6);
  return p ? { r: p.r, g: p.g, b: p.b, a: prev.a } : prev;
}

/** Replace alpha (0-1), preserving RGB. */
export function setAlpha(prev: Rgba, a: number): Rgba {
  return { r: prev.r, g: prev.g, b: prev.b, a: clamp(a, 0, 1) };
}
